import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import React from "react";
import { HgSpendCategory } from "../../../../types/taxonomy";
import {
  FilteredGeographyData,
  FilterLabel,
  IndustryNameData,
  ProductToolTipData,
} from "../types";
import { GeographyTooltipContent } from "./GeographyTooltipContent";
import { IndustryTooltipContent } from "./IndustryTooltipContent";
import { ProductsTooltipContent } from "./ProductsTooltipContent";
import WorkSpaceStyledTooltip from "./WorkSpaceStyledTooltip";
import WorkspaceSpendCategoryTooltipList from "./WorkspaceSpendCategoryTooltipList";
import { UITooltip } from "../../../../components/UITooltip";

interface MarketFilterProps {
  labels: FilterLabel[];
  categories: HgSpendCategory[];
  spendCategoryWeights: { [key: string]: number };
  geographyData: FilteredGeographyData;
  industryData: IndustryNameData;
  isLoading?: boolean;
  productData: ProductToolTipData;
}

const GetTooltipContent = (
  label: FilterLabel,
  categories: HgSpendCategory[],
  weights: { [key: string]: number },
  industryData: IndustryNameData,
  geographyData: FilteredGeographyData,
  productData: ProductToolTipData
) => {
  switch (label.type) {
    case "products":
      return <ProductsTooltipContent data={productData} />;
    case "spend":
      return (
        <WorkspaceSpendCategoryTooltipList
          categories={categories}
          weights={weights}
        />
      );
    case "industry":
      return <IndustryTooltipContent data={industryData} />;
    case "geography":
      return <GeographyTooltipContent data={geographyData} />;
    default:
      return null;
  }
};

export const SelectedMarketFilter: React.FC<MarketFilterProps> = ({
  labels,
  categories,
  spendCategoryWeights,
  industryData,
  geographyData,
  isLoading = false,
  productData,
}) => {
  return (
    <Stack
      id="selected-market-filters"
      alignItems="flex-start"
      sx={{
        flexBasis: "300px",
        flexGrow: 1,
        boxSizing: "border-box",
        border: "1px solid #eceff1",
        borderRadius: "7px",
        padding: "10px",
      }}
    >
      <Stack direction="row" marginBottom={1}>
        <UITooltip title="Market Filters">
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#757575" }} />
        </UITooltip>
        <Typography
          sx={{
            marginLeft: "8px",
            fontSize: "12px",
            fontWeight: "normal",
            color: "#757575",
          }}
        >
          How we’re filtering your data.
        </Typography>
      </Stack>
      <Box>
        {isLoading ? (
          <Stack direction="row" flexWrap="wrap" gap={1} ml={1}>
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                variant="rounded"
                width={100}
                height={24}
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "16px",
                }}
              />
            ))}
          </Stack>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {labels.map((label, index) => {
              const ChipContent = (
                <Chip
                  key={index}
                  label={label.text}
                  sx={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    color: "#000",
                    height: "18px",
                    borderRadius: "16px",
                    padding: "0 8px",
                    backgroundColor: "#f0f0f0",
                    cursor: "pointer",
                    "& .MuiChip-label": {
                      fontWeight: "normal",
                      lineHeight: "1.5",
                      letterSpacing: "0.16px",
                      padding: "3px 4px",
                    },
                  }}
                />
              );

              const tooltipContent = GetTooltipContent(
                label,
                categories,
                spendCategoryWeights,
                industryData,
                geographyData,
                productData
              );

              return tooltipContent ? (
                <WorkSpaceStyledTooltip
                  key={index}
                  title={tooltipContent}
                  arrow
                >
                  {ChipContent}
                </WorkSpaceStyledTooltip>
              ) : (
                ChipContent
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
